import { useSeo } from "../lib/seo";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Flame, Loader2, Lock, Sparkles } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePricingOffer, refreshPricingOffer, usd } from "../lib/pricing";
import { PromoCodeBox } from "../components/PromoCodeBox";

const freeFeatures = ["50 study questions a day in each track (Firefighter I & II, EMT, Promotional) from the full bank, with explanations", "Flashcards, practice & short timed exams — all drawing from your daily allowance", "Basic progress tracking, Road Map & resume editor", "Your first full Mock Oral Board free — voice or typed, with a summary report (one-time, does not renew); invite friends to earn more Voice boards", "Fitness Plan: evaluation, personalized plan, workouts, progress tracking & typed food log (AI photo analysis is Premium)",
];
const proFeatures = [
  "Full question bank — unlimited practice, flashcards & timed exams",
  "15 Mock Oral Boards a month (general or department-focused, voice or typed) with graded reports",
  "Resume Builder with PDF download & AI review",
  "Fitness Plan: AI food-photo calorie & macro analysis, higher plan/workout limits",
  "Career Road Map with AI next steps",
  "Full researched-department library — sourced profiles, flashcards, quizzes — plus 1 new department research a month and 5 favorites",
];

export default function Pricing() {
  useSeo({ title: "Pricing — Free vs Premium", description: "First Step is free to start. Premium ($19.99/month or $200/year) unlocks the full firefighter question bank, 15 graded mock oral boards a month, the researched-department library, resume PDF and AI review, and the Fitness Plan (beta).", path: "/pricing" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState(null); // validated server-side: { code, lookup_key, description, summary, first_cents, renewal_cents }
  const offer = usePricingOffer();
  const launch = offer.launch_active && !yearly;
  const lookup_key = yearly ? "pro_yearly" : "pro_monthly";
  const activePromo = promo && promo.lookup_key === lookup_key ? promo : null; // a Monthly code doesn't carry over to the Annual toggle (and vice versa)

  const subscribe = async () => {
    if (!user) { navigate("/auth?mode=register"); return; }
    if (user.is_premium) { toast.info("You're already a Premium member."); return; }
    setLoading(true);
    try {
      // Server clock is authoritative: re-check the offer right before Stripe so the page never promises $15 for a $19.99 session.
      const fresh = await refreshPricingOffer();
      if (fresh.launch_active !== offer.launch_active) {
        if (fresh.launch_active && activePromo) setPromo(null);
        toast.warning(fresh.launch_active ? "Good news — the launch price is now active. Please review the updated price and tap Go Premium again." : "The launch offer has ended, so pricing was updated. Please review and tap Go Premium again.", { duration: 9000 });
        setLoading(false);
        return;
      }
      const body = { lookup_key, origin_url: window.location.origin };
      if (activePromo?.code && !(fresh.launch_active && !yearly)) body.promo_code = activePromo.code;
      const { data } = await api.post("/payments/checkout", body);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };
  // What the checkout will actually charge (same server rule): shown before redirecting into Stripe.
  const summary = launch ? `${usd(offer.monthly_cents)}/month, locked in while you stay subscribed`
    : activePromo ? activePromo.summary
      : yearly ? `${usd(offer.yearly_cents)} today, renews yearly at ${usd(offer.yearly_cents)}`
        : `${usd(offer.monthly_regular_cents)}/month, cancel anytime`;

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1">
        <div className="bg-slate-950 tactical-grid">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <p className="text-xs font-semibold tracking-widest uppercase text-amber-400 mb-3">Pricing</p>
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold uppercase tracking-tight text-white">Invest in the badge</h1>
            <p className="mt-4 text-slate-300 max-w-xl mx-auto">Start free. Go Premium to unlock the AI mock oral board and the full question bank.</p>

            <div className="mt-8 inline-flex items-center gap-3 bg-slate-900 border border-slate-800 rounded-full px-5 py-2.5">
              <span className={`text-sm font-medium ${!yearly ? "text-white" : "text-slate-500"}`}>Monthly</span>
              <Switch checked={yearly} onCheckedChange={setYearly} data-testid="pricing-billing-toggle" />
              <span className={`text-sm font-medium ${yearly ? "text-white" : "text-slate-500"}`}>Yearly</span>
              <span className="text-xs bg-amber-500 text-slate-950 font-bold px-2 py-0.5 rounded-full">Save $40</span>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 pb-24">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Free */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-slate-200 rounded-2xl p-8 shadow-lg">
              <p className="uppercase text-xs tracking-widest text-slate-400 font-semibold">Free Recruit</p>
              <p className="font-heading text-5xl font-extrabold text-slate-900 mt-3">$0</p>
              <p className="text-slate-400 text-sm mt-1">Forever</p>
              <ul className="mt-6 space-y-3">
                {freeFeatures.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-slate-600"><CheckCircle2 className="w-5 h-5 text-slate-400 shrink-0" />{f}</li>
                ))}
              </ul>
              <Button onClick={() => navigate(user ? "/dashboard" : "/auth?mode=register")} variant="outline" className="w-full mt-8 h-12" data-testid="pricing-free-button">
                {user ? "Go to Study Bank" : "Start Free"}
              </Button>
            </motion.div>

            {/* Pro */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-gradient-to-b from-slate-900 to-slate-950 border-2 border-primary rounded-2xl p-8 shadow-2xl relative overflow-hidden">
              <span className="absolute top-6 right-6 bg-amber-500 text-slate-950 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Popular
              </span>
              <p className="uppercase text-xs tracking-widest text-amber-400 font-semibold">Premium</p>
              <div className="flex items-end gap-2 mt-3 flex-wrap">
                {launch && <p className="font-heading text-3xl font-bold text-slate-500 line-through mb-1" data-testid="pricing-regular-price">{usd(offer.monthly_regular_cents)}</p>}
                <p className="font-heading text-5xl font-extrabold text-white" data-testid="pricing-price">{yearly ? usd(offer.yearly_cents) : usd(offer.monthly_cents)}</p>
                <span className="text-slate-400 mb-2">/{yearly ? "year" : "month"}</span>
              </div>
              {launch
                ? <p className="text-amber-300 text-sm mt-1 font-medium" data-testid="pricing-launch-note">Limited-time launch price — lock in {usd(offer.monthly_cents)}/month while you stay subscribed.{offer.launch_ends_at_pacific ? ` Ends ${offer.launch_ends_at_pacific}.` : ""}</p>
                : <p className="text-slate-400 text-sm mt-1">{yearly ? "Save $40 a year — $16.67/mo billed annually" : "Cancel anytime"}</p>}
              <p className="text-slate-400 text-xs mt-1" data-testid="pricing-nfff-note">{yearly ? "$5 of every yearly payment" : "$0.50 of every monthly payment"} is set aside for the National Fallen Firefighters Foundation.</p>
              <ul className="mt-6 space-y-3">
                {proFeatures.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-slate-200"><CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" />{f}</li>
                ))}
              </ul>
              {user && !user.is_premium && <PromoCodeBox key={lookup_key} offer={offer} promo={activePromo} setPromo={setPromo} lookupKey={lookup_key} />}
              {user && !user.is_premium && <p className="mt-6 text-xs text-slate-400" data-testid="checkout-summary">You'll pay: <span className="text-white font-semibold">{summary}</span></p>}
              <Button onClick={subscribe} disabled={loading} className="w-full mt-3 h-12 bg-primary hover:bg-primary/90 font-semibold" data-testid="subscribe-button">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</> :
                  user?.is_premium ? <><CheckCircle2 className="w-4 h-4 mr-2" /> You're Premium</> :
                    <><Flame className="w-4 h-4 mr-2" /> Go Premium</>}
              </Button>
              <p className="text-center text-xs text-slate-500 mt-3">Secure checkout via Stripe</p>
            </motion.div>
          </div>

          {!user && (
            <p className="text-center text-slate-400 text-sm mt-8 flex items-center justify-center gap-2">
              <Lock className="w-4 h-4" /> You'll create an account before checkout.
            </p>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
